import Glide from "@glidejs/glide";
import useNcId from "hooks/useNcId";
import { FC, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRightIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  MapPinIcon,
} from "@heroicons/react/24/outline";
import { getClients } from "data/api";
import SafeImage from "components/SafeImage/SafeImage";

interface ClientItem {
  id: number;
  name: string;
  image: string;
  location: string;
}

const isValidClient = (client: {
  id: number;
  name: string;
  image: string;
}) => {
  if (!client.image || !client.name) return false;
  if (!client.image.includes("/Clients/")) return false;
  if (client.name.length < 3) return false;
  if (
    /^[a-z0-9]{1,12}$/i.test(client.name.replace(/\s/g, "")) &&
    client.name.length < 8
  )
    return false;
  return true;
};

const fallbackClients: ClientItem[] = [
  {
    id: 1,
    name: "Emaar",
    location: "Dubai, UAE",
    image:
      "https://bcrprofiles.s3.me-central-1.amazonaws.com/Clients/EMAAR+LOGO.png",
  },
  {
    id: 2,
    name: "Binghatti",
    location: "Dubai, UAE",
    image:
      "https://bcrprofiles.s3.me-central-1.amazonaws.com/Clients/binghatti+logo.jpg",
  },
  {
    id: 3,
    name: "Azizi",
    location: "Dubai, UAE",
    image:
      "https://bcrprofiles.s3.me-central-1.amazonaws.com/Clients/AZIZI+LOGO.png",
  },
  {
    id: 4,
    name: "Danube",
    location: "Dubai, UAE",
    image:
      "https://bcrprofiles.s3.me-central-1.amazonaws.com/Clients/Danube+LOGO.jpg",
  },
  {
    id: 5,
    name: "Meraas",
    location: "Dubai, UAE",
    image:
      "https://bcrprofiles.s3.me-central-1.amazonaws.com/Clients/MEERAS+LOGO.jpg",
  },
  {
    id: 6,
    name: "Sobha",
    location: "Dubai, UAE",
    image:
      "https://bcrprofiles.s3.me-central-1.amazonaws.com/Clients/SOBHA+LOGO.png",
  },
];

const SectionClientsLogos: FC = () => {
  const UNIQUE_CLASS = "ClientsShowcase" + useNcId();
  const [clients, setClients] = useState<ClientItem[]>(fallbackClients);
  const [activeIndex, setActiveIndex] = useState(0);
  const glideRef = useRef<Glide | null>(null);

  useEffect(() => {
    getClients().then((items) => {
      if (!Array.isArray(items)) return;
      const valid = items
        .filter(isValidClient)
        .map((c: any) => ({
          id: c.id,
          name: c.name,
          image: c.image,
          location: c.location ? `${c.location}, UAE` : "UAE",
        }))
        .slice(0, 12);
      if (valid.length > 0) setClients(valid);
    });
  }, []);

  useEffect(() => {
    const container = document.querySelector(`.${UNIQUE_CLASS}`);
    const slides = container?.querySelectorAll(".glide__slide");
    if (!container || !slides || slides.length === 0) return;

    const timer = setTimeout(() => {
      glideRef.current?.destroy();
      const instance = new Glide(`.${UNIQUE_CLASS}`, {
        type: "carousel",
        perView: 3,
        gap: 28,
        bound: true,
        focusAt: "center",
        autoplay: 4000,
        hoverpause: true,
        animationDuration: 700,
        breakpoints: {
          1280: { perView: 3, gap: 24, focusAt: "center" },
          1024: { perView: 2, gap: 20, focusAt: 0 },
          640: { perView: 1.15, gap: 16, focusAt: "center" },
        },
      });
      instance.mount();
      instance.on("run.after", () => setActiveIndex(instance.index));
      glideRef.current = instance;
    }, 80);
    return () => {
      clearTimeout(timer);
      glideRef.current?.destroy();
      glideRef.current = null;
    };
  }, [UNIQUE_CLASS, clients.length]);

  if (clients.length === 0) return null;

  return (
    <section className="relative py-16 sm:py-20 lg:py-28 bg-[#070f1f] overflow-hidden">
      {/* Background accents */}
      <div className="absolute inset-0 pointer-events-none bcr-clients-dots" />
      <div className="absolute top-1/3 -left-32 w-72 h-72 bg-primary-600/15 rounded-full blur-[100px]" />
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-secondary2-500/10 rounded-full blur-[120px]" />

      <div className="container relative px-4 sm:px-6 lg:px-8">
        {/* Header — team-style title */}
        <div className="text-center max-w-3xl mx-auto mb-12 sm:mb-14">
          <span className="text-xs font-semibold tracking-[0.25em] uppercase text-primary-400">
            Trusted Partners
          </span>
          <h2 className="mt-4 text-2xl sm:text-3xl lg:text-4xl font-bold text-white leading-snug">
            Meet Our{" "}
            <span className="inline-block px-3 py-0.5 sm:px-4 sm:py-1 border border-primary-500/60 rounded-md bg-primary-500/10 text-primary-300 shadow-[0_0_24px_rgba(59,130,246,0.2)]">
              Clients
            </span>
          </h2>
          <p className="mt-4 text-sm sm:text-base text-white/50 leading-relaxed">
            Proudly partnering with leading developers and contractors across
            the UAE and GCC — delivering UK-quality profiles on every landmark
            project.
          </p>
        </div>

        {/* Carousel controls — top right on desktop */}
        <div className="flex justify-center sm:justify-end gap-3 mb-8 sm:mb-0 sm:absolute sm:right-6 sm:top-16 lg:top-20 sm:z-10">
          <button
            type="button"
            onClick={() => glideRef.current?.go("<")}
            className="w-11 h-11 rounded-full border border-white/15 bg-white/5 hover:bg-white/10 text-white flex items-center justify-center transition-colors"
            aria-label="Previous clients"
          >
            <ChevronLeftIcon className="w-5 h-5" />
          </button>
          <button
            type="button"
            onClick={() => glideRef.current?.go(">")}
            className="w-11 h-11 rounded-full border border-white/15 bg-white/5 hover:bg-white/10 text-white flex items-center justify-center transition-colors"
            aria-label="Next clients"
          >
            <ChevronRightIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Team-style client cards */}
        <div className={`${UNIQUE_CLASS} relative mt-2 sm:mt-0`}>
          <div className="glide__track" data-glide-el="track">
            <ul className="glide__slides items-stretch">
              {clients.map((client) => (
                <li key={client.id} className="glide__slide h-auto py-4">
                  <div className="group h-full flex flex-col rounded-2xl overflow-hidden bg-[#0c1830] border border-white/10 hover:border-primary-500/40 transition-all duration-300 hover:shadow-[0_24px_60px_rgba(0,0,0,0.45)]">
                    {/* Gradient header strip */}
                    <div className="relative h-24 sm:h-28 bg-gradient-to-r from-secondary2-500 via-primary-600 to-primary-500 shrink-0" />

                    {/* Logo badge — overlaps header like team avatar */}
                    <div className="relative flex justify-center -mt-12 sm:-mt-14 px-4">
                      <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-full bg-white border-4 border-[#0c1830] shadow-xl flex items-center justify-center p-3 sm:p-4 group-hover:scale-105 transition-transform duration-300 overflow-hidden">
                        <SafeImage
                          src={client.image}
                          alt={client.name}
                          placeholderVariant="logo"
                          placeholderCompact
                          objectFit="contain"
                          className="w-full h-full"
                          imgClassName="max-w-full max-h-full"
                        />
                      </div>
                    </div>

                    {/* Card body */}
                    <div className="flex flex-col flex-1 px-5 sm:px-6 pt-3 pb-6 sm:pb-7 text-center">
                      <h3 className="text-lg sm:text-xl font-bold text-white group-hover:text-primary-300 transition-colors">
                        {client.name}
                      </h3>
                      <p className="mt-1.5 inline-flex items-center justify-center gap-1 text-sm font-medium text-primary-400">
                        <MapPinIcon className="w-3.5 h-3.5 shrink-0" />
                        {client.location}
                      </p>
                      <p className="mt-3 text-xs sm:text-sm text-white/45 leading-relaxed line-clamp-2">
                        Trusted BCR partner for expansion joints, tile trims &
                        architectural profiles.
                      </p>

                      <div className="mt-5 pt-4 border-t border-white/8">
                        <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-white/35 group-hover:text-primary-400 transition-colors">
                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                          Verified Partner
                        </span>
                      </div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Dots */}
        <div className="flex justify-center gap-2 mt-8">
          {clients.map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => glideRef.current?.go(`=${i}`)}
              className={`h-1.5 rounded-full transition-all duration-300 ${
                activeIndex === i
                  ? "w-8 bg-primary-400"
                  : "w-4 bg-white/20 hover:bg-white/40"
              }`}
              aria-label={`Go to client ${i + 1}`}
            />
          ))}
        </div>

        {/* CTA */}
        <div className="text-center mt-10">
          <Link
            to="/clients"
            className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full border border-white/20 bg-white/5 hover:bg-white/10 text-sm font-semibold text-white transition-all hover:border-primary-500/50"
          >
            View All Clients
            <ArrowRightIcon className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default SectionClientsLogos;
